import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOptiAssets } from '@/hooks/useOptiAssets';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Wrench, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WORK_TYPE_LABELS, type MaintenanceWorkType } from '@/hooks/useAssetMaintenance';

interface MaintenanceLogFormProps {
  assetId: string;
  planId?: string;
  trigger?: React.ReactNode;
}

export function MaintenanceLogForm({ assetId, planId, trigger }: MaintenanceLogFormProps) {
  const { activeShop, user } = useAuth();
  const { canLogUsage, canViewCosts } = useOptiAssets();
  const { shopTimezone } = useShopTimezone();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const [workType, setWorkType] = useState<MaintenanceWorkType>('maintenance');
  const [description, setDescription] = useState('');
  // R9-M-1: Use shop TZ for default date
  const [performedAt, setPerformedAt] = useState(() => getTodayInShopTz(shopTimezone));
  const [vendorName, setVendorName] = useState('');
  const [workCost, setWorkCost] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!activeShop) throw new Error('No active shop');

      // Create the maintenance log
      const { data: log, error: logError } = await supabase
        .from('shop_asset_maintenance_logs')
        .insert({
          asset_id: assetId,
          shop_id: activeShop.id,
          plan_id: planId || null,
          work_type: workType,
          description,
          performed_at: performedAt,
          performed_by: user?.id || null,
          vendor_name: vendorName || null,
        })
        .select('id')
        .single();

      if (logError) throw logError;

      // If manager and cost provided, create work cost record
      if (canViewCosts && workCost && parseFloat(workCost) > 0) {
        const { error: costError } = await supabase
          .from('shop_asset_work_costs')
          .insert({
            log_id: log.id,
            shop_id: activeShop.id,
            cost: parseFloat(workCost),
          });
        if (costError) throw costError;
      }

      // If linked to a plan, update next_due_at
      if (planId) {
        const { data: plan } = await supabase
          .from('shop_asset_maintenance_plans')
          .select('interval_days')
          .eq('id', planId)
          .single();

        if (plan?.interval_days) {
          const nextDue = new Date(performedAt);
          nextDue.setDate(nextDue.getDate() + plan.interval_days);

          await supabase
            .from('shop_asset_maintenance_plans')
            .update({ next_due_at: nextDue.toISOString() })
            .eq('id', planId);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-maintenance', assetId] });
      setOpen(false);
      resetForm();
      toast.success('Maintenance logged');
    },
    onError: (error) => {
      toast.error('Failed to log maintenance: ' + error.message);
    },
  });

  const resetForm = () => {
    setWorkType('maintenance');
    setDescription('');
    // R9-M-1: Use shop TZ for reset date
    setPerformedAt(getTodayInShopTz(shopTimezone));
    setVendorName('');
    setWorkCost('');
  };

  if (!canLogUsage) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="gap-2">
            <Wrench className="h-4 w-4" />
            Log Work
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log Maintenance Work</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Work Type *</Label>
            <Select value={workType} onValueChange={(v) => setWorkType(v as MaintenanceWorkType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WORK_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What work was performed..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date Performed</Label>
              <Input
                type="date"
                value={performedAt}
                onChange={(e) => setPerformedAt(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Vendor (optional)</Label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Vendor name"
              />
            </div>
          </div>

          {canViewCosts && (
            <div className="space-y-2">
              <Label>Cost (optional)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={workCost}
                  onChange={(e) => setWorkCost(e.target.value)}
                  placeholder="0.00"
                  className="pl-7"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Cost is only visible to managers
              </p>
            </div>
          )}

          <Button
            onClick={() => createMutation.mutate()}
            disabled={!description || createMutation.isPending}
            className="w-full"
          >
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Log Maintenance
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
